import { Bar, Slab, Sphere } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 1.8
const DEPTH = 0.5
const SEAT_HEIGHT = 0.45

const SLATS = 6
const SLAT_DEPTH = 0.062
const SLAT_GAP = 0.012
const SLAT_THICKNESS = 0.022
/** Centre-to-centre pitch of the slats. */
const SLAT_PITCH = SLAT_DEPTH + SLAT_GAP

const TUBE = 0.018
/** Cross rails sit 5 mm inside the slats so nothing is coplanar. */
const RAIL_Y = SEAT_HEIGHT - SLAT_THICKNESS - TUBE + 0.005
const FOOT_Y = TUBE
const FOOT_Z = 0.2
const RAIL_Z = 0.175

const WIDTHS: Record<string, number> = {
  '180': 1.8,
  '120': 1.2,
}

/**
 * One end frame: a tubular loop (foot runner, two splayed uprights, top cross
 * rail) with spheres filling the mitres so the bends read as continuous tube.
 */
function EndFrame({ x }: { x: number }) {
  return (
    <group>
      <Bar tone="metalDark" r={TUBE} from={[x, FOOT_Y, -FOOT_Z]} to={[x, FOOT_Y, FOOT_Z]} />
      {[-1, 1].map((side) => (
        <group key={side}>
          <Bar
            tone="metalDark"
            r={TUBE}
            from={[x, FOOT_Y, side * FOOT_Z]}
            to={[x, RAIL_Y, side * RAIL_Z]}
          />
          <Sphere tone="metalDark" r={TUBE} position={[x, FOOT_Y, side * FOOT_Z]} />
          <Sphere tone="metalDark" r={TUBE} position={[x, RAIL_Y, side * RAIL_Z]} />
        </group>
      ))}
      <Bar tone="metalDark" r={TUBE} from={[x, RAIL_Y, -RAIL_Z]} to={[x, RAIL_Y, RAIL_Z]} />
    </group>
  )
}

/**
 * Waiting bench: six white slats on a dark tubular frame, the classic corridor
 * / reception perch. Both variants share the 1.8 m footprint and the shorter
 * one is centred inside it, so snapping behaves the same either way.
 */
function BenchWaiting({ variant = '180' }: AssetContext) {
  const width = WIDTHS[variant] ?? WIDTH
  const frameX = width / 2 - 0.14
  const slatLength = width - 0.06

  return (
    <group>
      <EndFrame x={-frameX} />
      <EndFrame x={frameX} />

      {/* longitudinal stretcher tying the two end frames together */}
      <Bar tone="metalDark" r={0.015} from={[-frameX, 0.16, 0]} to={[frameX, 0.16, 0]} />

      {Array.from({ length: SLATS }, (_, index) => (
        <Slab
          key={index}
          tone="shell"
          size={[slatLength, SLAT_DEPTH, SLAT_THICKNESS]}
          radius={0.009}
          position={[0, SEAT_HEIGHT - SLAT_THICKNESS / 2, (index - (SLATS - 1) / 2) * SLAT_PITCH]}
        />
      ))}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'bench-waiting',
  name: 'Banca de espera',
  category: 'seating',
  footprint: [WIDTH, DEPTH],
  height: SEAT_HEIGHT,
  surfaceHeight: SEAT_HEIGHT,
  seats: 3,
  occupantSide: 'front',
  seatInset: 0.25,
  variants: [
    { id: '180', name: '1.8 m', footprint: [WIDTH, DEPTH] },
    { id: '120', name: '1.2 m', footprint: [1.2, DEPTH] },
  ],
  keywords: [
    'banca',
    'banco',
    'espera',
    'recepción',
    'listones',
    'bench',
    'waiting',
    'slatted',
    'lobby',
  ],
  Component: BenchWaiting,
}
